import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Separator } from '../components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import SendGridSetupWizard from '../components/SendGridSetupWizard';
import { 
  Mail, 
  Calendar,
  Check,
  X,
  ExternalLink,
  Loader2,
  AlertCircle,
  Building2,
  Save,
  Sparkles,
  MapPin,
  Truck,
  Plus,
  Trash2,
  Upload,
  ImageIcon,
  Globe,
  Shield,
  Send,
  Copy,
  RefreshCcw
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Settings = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [googleStatus, setGoogleStatus] = useState({ 
    connected: false, 
    loading: true, 
    needsReconnect: false,
    error: null 
  });
  const [connecting, setConnecting] = useState(false);
  
  // Admin & Access Control state
  const [adminStatus, setAdminStatus] = useState({ isAdmin: false, accessControlEnabled: false });
  const [accessControl, setAccessControl] = useState({ allowedEmails: [], registeredUsers: [] });
  const [newAllowedEmail, setNewAllowedEmail] = useState('');
  const [addingEmail, setAddingEmail] = useState(false);
  
  // Company Profile state
  const [companyProfile, setCompanyProfile] = useState({
    company_name: '',
    tagline: '',
    description: '',
    products_services: '',
    unique_value: '',
    target_customers: '',
    tone_style: 'professional',
    call_to_action: '',
    contact_name: '',
    contact_title: '',
    contact_phone: '',
    contact_email: '',
    website: '',
    // Bakery location
    bakery_address: '',
    bakery_city: '',
    bakery_state: '',
    bakery_zip: '',
    bakery_latitude: null,
    bakery_longitude: null,
    delivery_zones: []
  });
  const [profileLoading, setProfileLoading] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [geocodingBakery, setGeocodingBakery] = useState(false);
  const [calculatingDistances, setCalculatingDistances] = useState(false);
  const [distanceJob, setDistanceJob] = useState(null);

  // Delivery schedule state
  const [deliverySchedule, setDeliverySchedule] = useState({
    default_days: [],
    blocked_dates: [],
    extra_dates: [],
    weeks_ahead: 2
  });
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [newBlockedDate, setNewBlockedDate] = useState('');
  const [newExtraDate, setNewExtraDate] = useState('');

  // Logo upload state
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  // Category sync state (Sync Categories card)
  const [categoryMismatchCount, setCategoryMismatchCount] = useState(null);
  const [syncingCategories, setSyncingCategories] = useState(false);
  const [loadingMismatches, setLoadingMismatches] = useState(true);

  // Email provider / domain auth state
  const [emailProvider, setEmailProvider] = useState({ provider: 'gmail', loading: true });
  const [domainInput, setDomainInput] = useState('');
  const [initializingDomain, setInitializingDomain] = useState(false);
  const [verifyingDomain, setVerifyingDomain] = useState(false);
  const [setupInboundParse, setSetupInboundParse] = useState(false);
  const [inboundHostname, setInboundHostname] = useState('');
  const [wizardOpen, setWizardOpen] = useState(false);

  // Notification preferences state
  const [notifPrefs, setNotifPrefs] = useState(null);
  const [notifLoading, setNotifLoading] = useState(true);
  const [savingNotifPrefs, setSavingNotifPrefs] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [notifQueue, setNotifQueue] = useState({ events: [], unsent_count: 0 });

  useEffect(() => {
    checkGoogleStatus();
    fetchCompanyProfile();
    checkAdminStatus();
    fetchDeliverySchedule();
    fetchEmailProvider();
    fetchNotificationPreferences();
    fetchNotificationQueue();
    fetchCategoryMismatches();
    
    // Check for successful OAuth callback
    if (searchParams.get('google_connected') === 'true') {
      toast.success('Google connected successfully!');
      checkGoogleStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const fetchEmailProvider = async () => {
    try {
      const res = await axios.get(`${API}/settings/email-provider`, { withCredentials: true });
      setEmailProvider({ ...res.data, loading: false });
    } catch (err) {
      console.error('Failed to load email provider status:', err);
      setEmailProvider(prev => ({ ...prev, loading: false }));
    }
  };

  const fetchCategoryMismatches = async () => {
    setLoadingMismatches(true);
    try {
      const res = await axios.get(`${API}/leads/category-mismatches`, { withCredentials: true });
      setCategoryMismatchCount(res.data.count ?? 0);
    } catch (err) {
      console.error('Failed to load category mismatches:', err);
      setCategoryMismatchCount(null);
    } finally {
      setLoadingMismatches(false);
    }
  };

  const handleSyncCategories = async () => {
    setSyncingCategories(true);
    try {
      const res = await axios.post(`${API}/leads/sync-categories`, {}, { withCredentials: true });
      toast.success(res.data.message || `Synced ${res.data.updated} leads`);
      await fetchCategoryMismatches();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to sync categories');
    } finally {
      setSyncingCategories(false);
    }
  };

  const handleInitDomain = async () => {
    if (!domainInput.trim()) return;
    setInitializingDomain(true);
    try {
      const res = await axios.post(`${API}/settings/domain-auth`, { domain: domainInput.trim() }, { withCredentials: true });
      toast.success('Domain authentication initiated! Add the DNS records below.');
      setEmailProvider(prev => ({ ...prev, domain_auth: res.data }));
      setDomainInput('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to initiate domain authentication');
    } finally {
      setInitializingDomain(false);
    }
  };

  const handleVerifyDomain = async () => {
    setVerifyingDomain(true);
    try {
      const res = await axios.post(`${API}/settings/domain-auth/verify`, {}, { withCredentials: true });
      if (res.data.valid) {
        toast.success('Domain verified successfully!');
      } else {
        toast.error('DNS records not yet verified. Please check your DNS configuration and try again.');
      }
      fetchEmailProvider();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Verification failed');
    } finally {
      setVerifyingDomain(false);
    }
  };

  const handleSetupInboundParse = async () => {
    const hostname = inboundHostname.trim() || emailProvider.reply_to_domain;
    if (!hostname) return;
    setSetupInboundParse(true);
    try {
      const webhookUrl = `${BACKEND_URL}/api/webhooks/sendgrid/inbound`;
      await axios.post(`${API}/settings/inbound-parse`, { hostname, webhook_url: webhookUrl }, { withCredentials: true });
      toast.success('Inbound Parse configured! Replies will be received via webhook.');
      setInboundHostname('');
      fetchEmailProvider();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to setup Inbound Parse');
    } finally {
      setSetupInboundParse(false);
    }
  };

  const handleDeleteInboundParse = async (hostname) => {
    if (!window.confirm(`Remove Inbound Parse for ${hostname}?`)) return;
    try {
      await axios.delete(`${API}/settings/inbound-parse?hostname=${encodeURIComponent(hostname)}`, { withCredentials: true });
      toast.success('Inbound Parse removed');
      fetchEmailProvider();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove');
    }
  };

  const fetchNotificationPreferences = async () => {
    try {
      const res = await axios.get(`${API}/settings/notification-preferences`, { withCredentials: true });
      setNotifPrefs(res.data);
    } catch (err) {
      console.error('Failed to load notification preferences:', err);
    } finally {
      setNotifLoading(false);
    }
  };

  const fetchNotificationQueue = async () => {
    try {
      const res = await axios.get(`${API}/settings/notification-queue`, { withCredentials: true });
      setNotifQueue(res.data);
    } catch (err) {
      console.error('Failed to load notification queue:', err);
    }
  };

  const handleSaveNotifPrefs = async () => {
    if (!notifPrefs) return;
    setSavingNotifPrefs(true);
    try {
      await axios.put(`${API}/settings/notification-preferences`, {
        events: notifPrefs.events,
        batch_hour: notifPrefs.batch_hour
      }, { withCredentials: true });
      toast.success('Notification preferences saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save preferences');
    } finally {
      setSavingNotifPrefs(false);
    }
  };

  const handleSendDigest = async () => {
    setSendingDigest(true);
    try {
      const res = await axios.post(`${API}/settings/notification-preferences/send-digest`, {}, { withCredentials: true });
      if (res.data.status === 'empty') {
        toast.info('No pending notifications to send.');
      } else {
        toast.success(`Digest sent with ${res.data.count} events`);
      }
      fetchNotificationQueue();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send digest');
    } finally {
      setSendingDigest(false);
    }
  };

  const toggleNotifEvent = (eventKey, field, value) => {
    setNotifPrefs(prev => ({
      ...prev,
      events: {
        ...prev.events,
        [eventKey]: { ...prev.events[eventKey], [field]: value }
      }
    }));
  };

  const fetchDeliverySchedule = async () => {
    try {
      const res = await axios.get(`${API}/delivery-schedule`, { withCredentials: true });
      setDeliverySchedule(res.data);
    } catch (err) {
      // Delivery schedule may not exist yet for new users — 404 is expected
      if (err?.response?.status !== 404) {
        toast.error('Failed to load delivery schedule');
      }
    }
  };

  const saveDeliverySchedule = async () => {
    setSavingSchedule(true);
    try {
      await axios.put(`${API}/delivery-schedule`, deliverySchedule, { withCredentials: true });
      toast.success('Delivery schedule saved');
    } catch (err) {
      toast.error('Failed to save delivery schedule');
    } finally {
      setSavingSchedule(false);
    }
  };

  const toggleDay = (dayIndex) => {
    setDeliverySchedule(prev => ({
      ...prev,
      default_days: prev.default_days.includes(dayIndex)
        ? prev.default_days.filter(d => d !== dayIndex)
        : [...prev.default_days, dayIndex].sort()
    }));
  };

  const addBlockedDate = () => {
    if (newBlockedDate && !deliverySchedule.blocked_dates.includes(newBlockedDate)) {
      setDeliverySchedule(prev => ({ ...prev, blocked_dates: [...prev.blocked_dates, newBlockedDate].sort() }));
      setNewBlockedDate('');
    }
  };

  const addExtraDate = () => {
    if (newExtraDate && !deliverySchedule.extra_dates.includes(newExtraDate)) {
      setDeliverySchedule(prev => ({ ...prev, extra_dates: [...prev.extra_dates, newExtraDate].sort() }));
      setNewExtraDate('');
    }
  };

  const checkAdminStatus = async () => {
    try {
      const response = await axios.get(`${API}/admin/status`, { withCredentials: true });
      setAdminStatus({
        isAdmin: response.data.is_admin,
        accessControlEnabled: response.data.access_control_enabled
      });
      if (response.data.is_admin) {
        fetchAccessControl();
      }
    } catch (error) {
      console.error('Admin status check failed:', error);
    }
  };

  const fetchAccessControl = async () => {
    try {
      const response = await axios.get(`${API}/admin/access-control`, { withCredentials: true });
      setAccessControl({
        allowedEmails: response.data.allowed_emails || [],
        registeredUsers: response.data.registered_users || [],
        adminEmail: response.data.admin_email
      });
    } catch (error) {
      console.error('Access control fetch failed:', error);
    }
  };

  const handleAddAllowedEmail = async () => {
    if (!newAllowedEmail.trim()) return;
    setAddingEmail(true);
    try {
      await axios.post(`${API}/admin/access-control/add?email=${encodeURIComponent(newAllowedEmail)}`, {}, { withCredentials: true });
      toast.success(`Added ${newAllowedEmail} to allowed users`);
      setNewAllowedEmail('');
      fetchAccessControl();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add email');
    } finally {
      setAddingEmail(false);
    }
  };

  const handleRemoveAllowedEmail = async (email) => {
    if (!window.confirm(`Remove ${email} from allowed users? They will be logged out.`)) return;
    try {
      await axios.delete(`${API}/admin/access-control/remove?email=${encodeURIComponent(email)}`, { withCredentials: true });
      toast.success(`Removed ${email}`);
      fetchAccessControl();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove email');
    }
  };

  const checkGoogleStatus = async () => {
    try {
      const response = await axios.get(`${API}/oauth/google/status`, { withCredentials: true });
      setGoogleStatus({ 
        connected: response.data.connected, 
        loading: false,
        needsReconnect: response.data.needs_reconnect || false,
        hasRefreshToken: response.data.has_refresh_token,
        oauthConfigured: response.data.oauth_configured || false,
        error: response.data.error
      });
    } catch (error) {
      setGoogleStatus({ connected: false, loading: false, needsReconnect: false, oauthConfigured: false, error: null });
    }
  };

  const fetchCompanyProfile = async () => {
    try {
      const response = await axios.get(`${API}/company-profile`, { withCredentials: true });
      setCompanyProfile(response.data);
      // Fetch logo preview if one exists
      if (response.data.logo_storage_path) {
        fetchLogoPreview();
      }
    } catch (error) {
      toast.error('Failed to load company profile');
    } finally {
      setProfileLoading(false);
    }
  };

  const fetchLogoPreview = async () => {
    try {
      const response = await axios.get(`${API}/company-profile/logo`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const blobUrl = URL.createObjectURL(response.data);
      setLogoPreview(blobUrl);
    } catch (err) {
      console.error('Logo preview fetch failed:', err);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file (JPEG, PNG, WebP, or GIF)');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Logo must be under 5MB');
      return;
    }

    setUploadingLogo(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      await axios.post(`${API}/company-profile/logo`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Logo uploaded successfully!');
      // Show immediate preview from local file
      const localUrl = URL.createObjectURL(file);
      setLogoPreview(localUrl);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
      // Reset file input
      e.target.value = '';
    }
  };

  const handleGeocodeBakery = async () => {
    setGeocodingBakery(true);
    try {
      const response = await axios.post(`${API}/company-profile/geocode-bakery`, {}, { withCredentials: true });
      toast.success('Bakery location geocoded successfully!');
      setCompanyProfile(prev => ({
        ...prev,
        bakery_latitude: response.data.latitude,
        bakery_longitude: response.data.longitude
      }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to geocode bakery address');
    } finally {
      setGeocodingBakery(false);
    }
  };

  const handleCalculateDistances = async () => {
    setCalculatingDistances(true);
    try {
      const response = await axios.post(`${API}/leads/calculate-distances`, {}, { withCredentials: true });
      toast.success(`Distance calculation started for ${response.data.leads_count} leads`);
      setDistanceJob({ job_id: response.data.job_id, status: 'running' });
      pollDistanceJob(response.data.job_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start distance calculation');
      setCalculatingDistances(false);
    }
  };

  const pollDistanceJob = async (jobId) => {
    const interval = setInterval(async () => {
      try {
        const response = await axios.get(`${API}/leads/distance-job/${jobId}`, { withCredentials: true });
        setDistanceJob(response.data);
        
        if (response.data.status === 'completed') {
          clearInterval(interval);
          setCalculatingDistances(false);
          toast.success(`Distance calculation complete! Processed ${response.data.processed} leads, geocoded ${response.data.geocoded} new addresses.`);
        } else if (response.data.status === 'failed') {
          clearInterval(interval);
          setCalculatingDistances(false);
          toast.error('Distance calculation failed');
        }
      } catch (error) {
        clearInterval(interval);
        setCalculatingDistances(false);
      }
    }, 2000);
  };

  const addDeliveryZone = () => {
    setCompanyProfile(prev => ({
      ...prev,
      delivery_zones: [...(prev.delivery_zones || []), { name: '', max_miles: 0, delivery_fee: 0 }]
    }));
  };

  const updateDeliveryZone = (index, field, value) => {
    setCompanyProfile(prev => ({
      ...prev,
      delivery_zones: prev.delivery_zones.map((zone, i) => 
        i === index ? { ...zone, [field]: value } : zone
      )
    }));
  };

  const removeDeliveryZone = (index) => {
    setCompanyProfile(prev => ({
      ...prev,
      delivery_zones: prev.delivery_zones.filter((_, i) => i !== index)
    }));
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      await axios.put(`${API}/company-profile`, companyProfile, { withCredentials: true });
      toast.success('Company profile saved!');
    } catch (error) {
      toast.error('Failed to save profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleConnectGoogle = async () => {
    setConnecting(true);
    try {
      const response = await axios.get(`${API}/oauth/google/login`, { withCredentials: true });
      if (response.data.authorization_url) {
        window.location.href = response.data.authorization_url;
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to start Google OAuth';
      toast.error(message);
      setConnecting(false);
    }
  };

  const handleDisconnectGoogle = async () => {
    if (!window.confirm('Disconnect Google? You will need to reconnect to send emails and schedule calendar events.')) return;
    
    try {
      await axios.post(`${API}/oauth/google/disconnect`, {}, { withCredentials: true });
      toast.success('Google disconnected');
      setGoogleStatus({ connected: false, loading: false });
    } catch (error) {
      toast.error('Failed to disconnect');
    }
  };

  const updateProfile = (field, value) => {
    setCompanyProfile(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="space-y-6 animate-fade-in max-w-4xl" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="font-heading font-bold text-2xl sm:text-3xl text-foreground">Settings</h1>
        <p className="text-muted-foreground mt-1">Manage your account, integrations, and email outreach settings</p>
      </div>

      {/* Profile */}
      <Card className="bg-white border-stone-100">
        <CardHeader>
          <CardTitle className="font-heading text-lg">Profile</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <Avatar className="h-16 w-16">
              <AvatarImage src={user?.picture} alt={user?.name} />
              <AvatarFallback className="bg-forest text-white text-xl">
                {user?.name?.charAt(0) || 'U'}
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="font-medium text-lg">{user?.name}</p>
              <p className="text-muted-foreground">{user?.email}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Company Profile for Email Outreach */}
      <Card className="bg-white border-stone-100">
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Building2 className="h-5 w-5 text-forest" />
            Email Outreach Profile
          </CardTitle>
          <CardDescription>
            This information is used by AI to craft personalized outreach emails. The more details you provide, the better your emails will be.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {profileLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              {/* Company Basics */}
              <div className="space-y-4">
                <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">Company Information</h3>
                
                {/* Logo Upload */}
                <div className="flex items-start gap-5 p-4 rounded-lg border border-dashed border-stone-200 bg-stone-50/50">
                  <div className="shrink-0">
                    {logoPreview ? (
                      <img 
                        src={logoPreview} 
                        alt="Company logo" 
                        className="h-20 w-20 rounded-lg object-contain border border-stone-200 bg-white p-1"
                        data-testid="logo-preview"
                      />
                    ) : (
                      <div className="h-20 w-20 rounded-lg border border-stone-200 bg-white flex items-center justify-center" data-testid="logo-placeholder">
                        <ImageIcon className="h-8 w-8 text-stone-300" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <Label className="text-sm font-medium">Company Logo</Label>
                    <p className="text-xs text-muted-foreground mt-0.5 mb-3">
                      Used in outreach emails, delivery confirmations, and the scheduling page. Max 5MB.
                    </p>
                    <label 
                      className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md border border-stone-200 bg-white hover:bg-stone-50 cursor-pointer transition-colors"
                      data-testid="logo-upload-button"
                    >
                      {uploadingLogo ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Upload className="h-4 w-4" />
                      )}
                      {uploadingLogo ? 'Uploading...' : logoPreview ? 'Replace Logo' : 'Upload Logo'}
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        onChange={handleLogoUpload}
                        className="sr-only"
                        disabled={uploadingLogo}
                        data-testid="logo-file-input"
                      />
                    </label>
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="company_name">Company Name</Label>
                    <Input
                      id="company_name"
                      value={companyProfile.company_name}
                      onChange={(e) => updateProfile('company_name', e.target.value)}
                      placeholder="e.g., Sunrise Wholesale Bakery"
                      data-testid="input-company-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="website">Website</Label>
                    <Input
                      id="website"
                      value={companyProfile.website}
                      onChange={(e) => updateProfile('website', e.target.value)}
                      placeholder="https://yourbakery.com"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="tagline">Tagline / Elevator Pitch</Label>
                  <Input
                    id="tagline"
                    value={companyProfile.tagline}
                    onChange={(e) => updateProfile('tagline', e.target.value)}
                    placeholder="e.g., Fresh artisan breads delivered to your door every morning"
                  />
                  <p className="text-xs text-muted-foreground mt-1">A short, catchy description of what you do</p>
                </div>
                <div>
                  <Label htmlFor="description">About Your Business</Label>
                  <Textarea
                    id="description"
                    value={companyProfile.description}
                    onChange={(e) => updateProfile('description', e.target.value)}
                    placeholder="Tell your story - how did you start? What's your mission? What makes your bakery special?"
                    rows={3}
                  />
                </div>
              </div>

              <Separator />

              {/* Products & Value */}
              <div className="space-y-4">
                <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">Products & Value Proposition</h3>
                <div>
                  <Label htmlFor="products_services">Products & Services</Label>
                  <Textarea
                    id="products_services"
                    value={companyProfile.products_services}
                    onChange={(e) => updateProfile('products_services', e.target.value)}
                    placeholder="e.g., Artisan sourdough breads, croissants, muffins, custom wedding cakes, wholesale pastry platters, daily delivery service..."
                    rows={3}
                  />
                  <p className="text-xs text-muted-foreground mt-1">List your main offerings that you want to highlight in outreach</p>
                </div>
                <div>
                  <Label htmlFor="unique_value">What Makes You Different?</Label>
                  <Textarea
                    id="unique_value"
                    value={companyProfile.unique_value}
                    onChange={(e) => updateProfile('unique_value', e.target.value)}
                    placeholder="e.g., Family-owned since 1985, organic ingredients only, 4am delivery guarantee, custom orders with 24hr notice..."
                    rows={2}
                  />
                  <p className="text-xs text-muted-foreground mt-1">Your competitive advantages and unique selling points</p>
                </div>
                <div>
                  <Label htmlFor="target_customers">Ideal Customers</Label>
                  <Textarea
                    id="target_customers"
                    value={companyProfile.target_customers}
                    onChange={(e) => updateProfile('target_customers', e.target.value)}
                    placeholder="e.g., We specialize in serving coffee shops, boutique hotels, and catering companies who value quality over price..."
                    rows={2}
                  />
                </div>
              </div>

              <Separator />

              {/* Outreach Preferences */}
              <div className="space-y-4">
                <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">Outreach Preferences</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="tone_style">Email Tone</Label>
                    <Select
                      value={companyProfile.tone_style}
                      onValueChange={(value) => updateProfile('tone_style', value)}
                    >
                      <SelectTrigger data-testid="select-tone">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="professional">Professional</SelectItem>
                        <SelectItem value="friendly">Friendly & Warm</SelectItem>
                        <SelectItem value="casual">Casual & Conversational</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground mt-1">How should your emails sound?</p>
                  </div>
                  <div>
                    <Label htmlFor="call_to_action">Default Call-to-Action</Label>
                    <Input
                      id="call_to_action"
                      value={companyProfile.call_to_action}
                      onChange={(e) => updateProfile('call_to_action', e.target.value)}
                      placeholder="e.g., Schedule a tasting, Request a quote, Reply to learn more"
                    />
                  </div>
                </div>
              </div>

              <Separator />

              {/* Contact / Signature */}
              <div className="space-y-4">
                <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">Email Signature</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="contact_name">Your Name</Label>
                    <Input
                      id="contact_name"
                      value={companyProfile.contact_name}
                      onChange={(e) => updateProfile('contact_name', e.target.value)}
                      placeholder={user?.name || 'Your name'}
                    />
                  </div>
                  <div>
                    <Label htmlFor="contact_title">Your Title</Label>
                    <Input
                      id="contact_title"
                      value={companyProfile.contact_title}
                      onChange={(e) => updateProfile('contact_title', e.target.value)}
                      placeholder="e.g., Owner, Sales Manager, Account Executive"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contact_email">Reply-to Email</Label>
                    <Input
                      id="contact_email"
                      value={companyProfile.contact_email}
                      onChange={(e) => updateProfile('contact_email', e.target.value)}
                      placeholder={user?.email || 'your@email.com'}
                    />
                  </div>
                  <div>
                    <Label htmlFor="contact_phone">Phone Number</Label>
                    <Input
                      id="contact_phone"
                      value={companyProfile.contact_phone}
                      onChange={(e) => updateProfile('contact_phone', e.target.value)}
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </div>
              </div>

              {/* Preview hint */}
              <div className="p-4 bg-sage/30 rounded-lg border border-sage">
                <div className="flex items-start gap-3">
                  <Sparkles className="h-5 w-5 text-wheat flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-sm">How this is used</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      When you draft an email for a lead, Claude AI uses this information to write personalized, 
                      relevant outreach that highlights your products and speaks to what matters to each customer type.
                    </p>
                  </div>
                </div>
              </div>

              {/* Save Button */}
              <Button
                onClick={handleSaveProfile}
                disabled={savingProfile}
                className="w-full sm:w-auto bg-forest hover:bg-forest-dark text-white"
                data-testid="save-profile-btn"
              >
                {savingProfile ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Save Profile
                  </>
                )}
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      {/* Bakery Location & Delivery Zones */}
      <Card className="bg-white border-stone-100">
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <MapPin className="h-5 w-5 text-forest" />
            Bakery Location & Delivery Zones
          </CardTitle>
          <CardDescription>
            Set your bakery address to calculate distances to leads for delivery logistics planning
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Bakery Address */}
          <div className="space-y-4">
            <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">Your Bakery Location</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Label htmlFor="bakery_address">Street Address</Label>
                <Input
                  id="bakery_address"
                  value={companyProfile.bakery_address || ''}
                  onChange={(e) => updateProfile('bakery_address', e.target.value)}
                  placeholder="123 Main Street"
                />
              </div>
              <div>
                <Label htmlFor="bakery_city">City</Label>
                <Input
                  id="bakery_city"
                  value={companyProfile.bakery_city || ''}
                  onChange={(e) => updateProfile('bakery_city', e.target.value)}
                  placeholder="White Plains"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="bakery_state">State</Label>
                  <Input
                    id="bakery_state"
                    value={companyProfile.bakery_state || ''}
                    onChange={(e) => updateProfile('bakery_state', e.target.value)}
                    placeholder="NY"
                  />
                </div>
                <div>
                  <Label htmlFor="bakery_zip">ZIP</Label>
                  <Input
                    id="bakery_zip"
                    value={companyProfile.bakery_zip || ''}
                    onChange={(e) => updateProfile('bakery_zip', e.target.value)}
                    placeholder="10601"
                  />
                </div>
              </div>
            </div>

            {/* Geocode Status & Button */}
            <div className="flex items-center justify-between p-4 bg-sage/30 rounded-lg">
              <div>
                <p className="font-medium text-sm">Location Coordinates</p>
                {companyProfile.bakery_latitude && companyProfile.bakery_longitude ? (
                  <p className="text-sm text-green-600 flex items-center gap-1">
                    <Check className="h-4 w-4" />
                    Geocoded: {companyProfile.bakery_latitude.toFixed(4)}, {companyProfile.bakery_longitude.toFixed(4)}
                  </p>
                ) : (
                  <p className="text-sm text-muted-foreground">Not yet geocoded</p>
                )}
              </div>
              <Button
                onClick={handleGeocodeBakery}
                disabled={geocodingBakery || !companyProfile.bakery_address}
                variant="outline"
                size="sm"
              >
                {geocodingBakery ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <MapPin className="h-4 w-4 mr-2" />
                )}
                {companyProfile.bakery_latitude ? 'Re-geocode' : 'Geocode Address'}
              </Button>
            </div>
          </div>

          <Separator />

          {/* Delivery Zones */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">Delivery Zones</h3>
                <p className="text-xs text-muted-foreground mt-1">Define zones based on distance for pricing and logistics</p>
              </div>
              <Button onClick={addDeliveryZone} variant="outline" size="sm">
                <Plus className="h-4 w-4 mr-1" />
                Add Zone
              </Button>
            </div>

            {(!companyProfile.delivery_zones || companyProfile.delivery_zones.length === 0) ? (
              <div className="text-center py-6 bg-stone-50 rounded-lg">
                <Truck className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">No delivery zones configured</p>
                <p className="text-xs text-muted-foreground">Default zones (0-10, 10-20, 20-30 miles) will be used</p>
              </div>
            ) : (
              <div className="space-y-3">
                {companyProfile.delivery_zones.map((zone, index) => (
                  <div key={`zone-${zone.name || index}`} className="flex items-center gap-3 p-3 bg-stone-50 rounded-lg">
                    <div className="flex-1">
                      <Input
                        value={zone.name}
                        onChange={(e) => updateDeliveryZone(index, 'name', e.target.value)}
                        placeholder="Zone A"
                        className="mb-2"
                      />
                      <div className="flex gap-2">
                        <div className="flex-1">
                          <Label className="text-xs">Max Miles</Label>
                          <Input
                            type="number"
                            value={zone.max_miles}
                            onChange={(e) => updateDeliveryZone(index, 'max_miles', parseFloat(e.target.value) || 0)}
                            placeholder="10"
                          />
                        </div>
                        <div className="flex-1">
                          <Label className="text-xs">Delivery Fee ($)</Label>
                          <Input
                            type="number"
                            value={zone.delivery_fee}
                            onChange={(e) => updateDeliveryZone(index, 'delivery_fee', parseFloat(e.target.value) || 0)}
                            placeholder="0"
                          />
                        </div>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeDeliveryZone(index)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <Separator />

          {/* Calculate Distances */}
          <div className="space-y-4">
            <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">Calculate Lead Distances</h3>
            <div className="p-4 bg-sage/30 rounded-lg">
              <div className="flex items-start gap-3">
                <Truck className="h-5 w-5 text-forest flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium text-sm">Batch Distance Calculation</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Calculate distances from your bakery to all leads. This helps you plan delivery routes 
                    and prioritize leads by proximity. Uses free geocoding (may take a few minutes for many leads).
                  </p>
                  
                  {distanceJob && distanceJob.status === 'running' && (
                    <div className="mt-3 p-3 bg-white rounded border">
                      <div className="flex items-center gap-2 mb-2">
                        <Loader2 className="h-4 w-4 animate-spin text-forest" />
                        <span className="text-sm font-medium">Processing...</span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Processed {distanceJob.processed || 0} of {distanceJob.total_leads || 0} leads
                        {distanceJob.geocoded > 0 && ` (${distanceJob.geocoded} newly geocoded)`}
                      </p>
                    </div>
                  )}
                  
                  {distanceJob && distanceJob.status === 'completed' && (
                    <div className="mt-3 p-3 bg-green-50 rounded border border-green-200">
                      <p className="text-sm text-green-700 flex items-center gap-1">
                        <Check className="h-4 w-4" />
                        Completed! Processed {distanceJob.processed} leads, geocoded {distanceJob.geocoded} new addresses.
                      </p>
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-4">
                <Button
                  onClick={handleCalculateDistances}
                  disabled={calculatingDistances || !companyProfile.bakery_latitude}
                  className="bg-forest hover:bg-forest-dark text-white"
                >
                  {calculatingDistances ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Calculating...
                    </>
                  ) : (
                    <>
                      <MapPin className="mr-2 h-4 w-4" />
                      Calculate All Distances
                    </>
                  )}
                </Button>
                {!companyProfile.bakery_latitude && (
                  <p className="text-xs text-amber-600 mt-2">
                    Please save your bakery address and geocode it first
                  </p>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Delivery Schedule */}
      <Card className="bg-white border-stone-100" data-testid="delivery-schedule-section">
        <CardHeader>
          <CardTitle className="font-heading text-lg">Delivery Schedule</CardTitle>
          <CardDescription>Define which days complimentary boxes can be delivered</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Default delivery days */}
          <div>
            <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide mb-3">Default Delivery Days</h3>
            <div className="flex flex-wrap gap-2">
              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, idx) => (
                <Button
                  key={day}
                  variant={deliverySchedule.default_days.includes(idx) ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => toggleDay(idx)}
                  className={deliverySchedule.default_days.includes(idx) ? 'bg-forest hover:bg-forest-dark text-white' : ''}
                  data-testid={`day-toggle-${idx}`}
                >
                  {day}
                </Button>
              ))}
            </div>
          </div>

          {/* Blocked dates */}
          <div>
            <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide mb-3">Blocked Dates</h3>
            <p className="text-xs text-muted-foreground mb-2">Dates when delivery is NOT available (holidays, etc.)</p>
            <div className="flex gap-2 mb-2">
              <Input type="date" value={newBlockedDate} onChange={(e) => setNewBlockedDate(e.target.value)} className="max-w-[180px]" />
              <Button variant="outline" size="sm" onClick={addBlockedDate}>Block Date</Button>
            </div>
            <div className="flex flex-wrap gap-1">
              {deliverySchedule.blocked_dates.map(d => (
                <Badge key={d} variant="secondary" className="cursor-pointer bg-red-100 text-red-700 hover:bg-red-200"
                  onClick={() => setDeliverySchedule(prev => ({ ...prev, blocked_dates: prev.blocked_dates.filter(x => x !== d) }))}>
                  {d} x
                </Badge>
              ))}
            </div>
          </div>

          {/* Extra dates */}
          <div>
            <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide mb-3">Extra Delivery Dates</h3>
            <p className="text-xs text-muted-foreground mb-2">One-off dates added outside the default schedule</p>
            <div className="flex gap-2 mb-2">
              <Input type="date" value={newExtraDate} onChange={(e) => setNewExtraDate(e.target.value)} className="max-w-[180px]" />
              <Button variant="outline" size="sm" onClick={addExtraDate}>Add Date</Button>
            </div>
            <div className="flex flex-wrap gap-1">
              {deliverySchedule.extra_dates.map(d => (
                <Badge key={d} variant="secondary" className="cursor-pointer bg-green-100 text-green-700 hover:bg-green-200"
                  onClick={() => setDeliverySchedule(prev => ({ ...prev, extra_dates: prev.extra_dates.filter(x => x !== d) }))}>
                  {d} x
                </Badge>
              ))}
            </div>
          </div>

          <Button onClick={saveDeliverySchedule} disabled={savingSchedule} className="bg-forest hover:bg-forest-dark text-white" data-testid="save-schedule-btn">
            {savingSchedule ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving...</> : 'Save Delivery Schedule'}
          </Button>
        </CardContent>
      </Card>

      {/* Google Calendar */}
      <Card className="bg-white border-stone-100">
        <CardHeader>
          <CardTitle className="font-heading text-lg">Google Calendar</CardTitle>
          <CardDescription>
            Connect Google to automatically create calendar entries when clients schedule a delivery or meeting from your outreach emails.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Calendar */}
          <div className="flex items-center justify-between p-4 rounded-lg bg-sage/50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                <Calendar className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <p className="font-medium">Google Calendar</p>
                <p className="text-sm text-muted-foreground">Auto-creates entries when clients book deliveries or meetings</p>
              </div>
            </div>
            {googleStatus.loading ? (
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            ) : googleStatus.connected ? (
              <Badge className="bg-green-600">
                <Check className="h-3 w-3 mr-1" />
                Connected
              </Badge>
            ) : (
              <Badge variant="secondary">Not connected</Badge>
            )}
          </div>

          <Separator />

          {/* Needs Reconnect Warning */}
          {googleStatus.connected && googleStatus.needsReconnect && (
            <div className="p-4 rounded-lg bg-red-50 border border-red-200">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-red-800">Reconnection Required</p>
                  <p className="text-sm text-red-700 mt-1">
                    Your Google authorization has expired or was revoked. Please disconnect and reconnect 
                    to restore email sending and calendar functionality.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Connect/Disconnect Button */}
          {googleStatus.connected ? (
            <div className="space-y-3">
              {googleStatus.needsReconnect ? (
                <Button 
                  onClick={handleDisconnectGoogle}
                  className="w-full bg-red-600 hover:bg-red-700 text-white"
                  data-testid="reconnect-google-btn"
                >
                  <X className="mr-2 h-4 w-4" />
                  Disconnect & Reconnect Google
                </Button>
              ) : (
                <Button 
                  variant="outline" 
                  onClick={handleDisconnectGoogle}
                  className="w-full text-destructive border-destructive hover:bg-destructive/10"
                  data-testid="disconnect-google-btn"
                >
                  <X className="mr-2 h-4 w-4" />
                  Disconnect Google
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {!googleStatus.oauthConfigured ? (
                <div className="p-4 rounded-lg bg-amber-50 border border-amber-200">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-800">Setup Required</p>
                      <p className="text-sm text-amber-700 mt-1">
                        To connect Gmail and Calendar, you need Google OAuth credentials. 
                        Please set up GOOGLE_CLIENT_ID and GOOGLE_CLIENT_SECRET in your environment.
                      </p>
                      <a 
                        href="https://console.cloud.google.com" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-sm text-amber-800 hover:underline mt-2"
                      >
                        Google Cloud Console
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
                  <div className="flex items-start gap-3">
                    <Mail className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-blue-800">Ready to Connect</p>
                      <p className="text-sm text-blue-700 mt-1">
                        Google OAuth is configured. Click below to authorize Rise to send emails and manage your calendar.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              <Button 
                onClick={handleConnectGoogle}
                disabled={connecting || !googleStatus.oauthConfigured}
                className="w-full bg-forest hover:bg-forest-dark text-white"
                data-testid="connect-google-btn"
              >
                {connecting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Connecting...
                  </>
                ) : (
                  <>
                    <Mail className="mr-2 h-4 w-4" />
                    Connect Google Account
                  </>
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Email Domain Setup (Admin Only) */}
      {adminStatus.isAdmin && (
        <Card className="bg-white border-stone-100" data-testid="email-domain-setup">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <Globe className="h-5 w-5 text-forest" />
                  Email Domain Setup
                  <Badge variant="outline" className="ml-2 text-xs">
                    {emailProvider.loading ? '...' : emailProvider.provider?.toUpperCase()}
                  </Badge>
                </CardTitle>
                <CardDescription className="mt-1">
                  Configure SendGrid domain authentication for reliable email delivery. Your emails will be sent from <strong>{emailProvider.sender_email || 'noreply@yourdomain.com'}</strong>.
                </CardDescription>
              </div>
              <Button
                onClick={() => setWizardOpen(true)}
                size="sm"
                className="bg-forest hover:bg-forest-dark text-white shrink-0"
                data-testid="open-sendgrid-wizard-btn"
              >
                <Sparkles className="mr-2 h-3.5 w-3.5" />
                Setup Wizard
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-5">
            {emailProvider.loading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <>
                {/* Provider Status */}
                <div className="flex items-center justify-between p-4 rounded-lg bg-sage/50">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                      <Send className="h-5 w-5 text-forest" />
                    </div>
                    <div>
                      <p className="font-medium">SendGrid</p>
                      <p className="text-sm text-muted-foreground">
                        {emailProvider.provider === 'sendgrid' ? 'Active email provider' : 'Available (switch via EMAIL_PROVIDER env)'}
                      </p>
                    </div>
                  </div>
                  {emailProvider.sendgrid_configured ? (
                    <Badge className={emailProvider.provider === 'sendgrid' ? 'bg-green-600' : 'bg-stone-400'}>
                      <Check className="h-3 w-3 mr-1" />
                      {emailProvider.provider === 'sendgrid' ? 'Active' : 'Configured'}
                    </Badge>
                  ) : (
                    <Badge variant="secondary">Not configured</Badge>
                  )}
                </div>

                {emailProvider.reply_to_domain && (
                  <div className="p-3 rounded-lg bg-stone-50 border border-stone-200">
                    <p className="text-xs text-muted-foreground mb-1">Reply-To Domain</p>
                    <p className="text-sm font-mono">{emailProvider.reply_to_domain}</p>
                  </div>
                )}

                <Separator />

                {/* Domain Authentication */}
                <div>
                  <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide mb-3">Domain Authentication</h3>
                  
                  {emailProvider.domain_auth ? (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-3 rounded-lg border border-stone-200">
                        <div className="flex items-center gap-2">
                          <Shield className={`h-4 w-4 ${emailProvider.domain_auth.valid ? 'text-green-600' : 'text-amber-500'}`} />
                          <span className="font-medium text-sm">{emailProvider.domain_auth.domain}</span>
                        </div>
                        <Badge className={emailProvider.domain_auth.valid ? 'bg-green-600' : 'bg-amber-500'}>
                          {emailProvider.domain_auth.valid ? 'Verified' : 'Pending'}
                        </Badge>
                      </div>

                      {/* DNS Records Table */}
                      {emailProvider.domain_auth.dns_records?.length > 0 && (
                        <div className="border border-stone-200 rounded-lg overflow-hidden">
                          <div className="bg-stone-50 px-4 py-2 border-b border-stone-200">
                            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">DNS Records to Configure</p>
                          </div>
                          <div className="divide-y divide-stone-100">
                            {emailProvider.domain_auth.dns_records.map((record, idx) => (
                              <div key={record.host || record.name || idx} className="px-4 py-3">
                                <div className="flex items-center gap-2 mb-1">
                                  <Badge variant="outline" className="text-[10px] font-mono">{record.type?.toUpperCase() || 'CNAME'}</Badge>
                                  <span className={`text-xs ${record.valid ? 'text-green-600' : 'text-amber-600'}`}>
                                    {record.valid ? 'Valid' : 'Pending'}
                                  </span>
                                </div>
                                <div className="grid gap-1 mt-1">
                                  <div className="flex items-center gap-1">
                                    <span className="text-xs text-muted-foreground w-12 shrink-0">Host:</span>
                                    <code className="text-xs bg-stone-100 px-1.5 py-0.5 rounded font-mono break-all">{record.host}</code>
                                    <button
                                      onClick={() => { navigator.clipboard.writeText(record.host); toast.success('Copied!'); }}
                                      className="shrink-0 text-muted-foreground hover:text-foreground"
                                      data-testid={`copy-host-${idx}`}
                                    >
                                      <Copy className="h-3 w-3" />
                                    </button>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <span className="text-xs text-muted-foreground w-12 shrink-0">Value:</span>
                                    <code className="text-xs bg-stone-100 px-1.5 py-0.5 rounded font-mono break-all">{record.data}</code>
                                    <button
                                      onClick={() => { navigator.clipboard.writeText(record.data); toast.success('Copied!'); }}
                                      className="shrink-0 text-muted-foreground hover:text-foreground"
                                      data-testid={`copy-value-${idx}`}
                                    >
                                      <Copy className="h-3 w-3" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {!emailProvider.domain_auth.valid && (
                        <Button
                          onClick={handleVerifyDomain}
                          disabled={verifyingDomain}
                          className="w-full bg-forest hover:bg-forest-dark text-white"
                          data-testid="verify-domain-btn"
                        >
                          {verifyingDomain ? (
                            <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Verifying DNS...</>
                          ) : (
                            <><Shield className="mr-2 h-4 w-4" />Verify Domain</>
                          )}
                        </Button>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <p className="text-sm text-muted-foreground">
                        Authenticate your domain to improve email deliverability and avoid spam filters. 
                        This sets up SPF, DKIM, and DMARC records via SendGrid.
                      </p>
                      <div className="flex gap-2">
                        <Input
                          placeholder="yourdomain.com"
                          value={domainInput}
                          onChange={(e) => setDomainInput(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && handleInitDomain()}
                          data-testid="domain-input"
                        />
                        <Button
                          onClick={handleInitDomain}
                          disabled={initializingDomain || !domainInput.trim()}
                          className="bg-forest hover:bg-forest-dark text-white shrink-0"
                          data-testid="init-domain-btn"
                        >
                          {initializingDomain ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Authenticate'}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                <Separator />

                {/* Inbound Parse (Reply Webhook) */}
                <div>
                  <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide mb-3">Inbound Parse (Reply Webhook)</h3>
                  
                  {(() => {
                    const parseSettings = Array.isArray(emailProvider.inbound_parse) ? emailProvider.inbound_parse : [];
                    const hasActive = parseSettings.length > 0;
                    
                    return hasActive ? (
                      <div className="space-y-3">
                        {parseSettings.map((setting, idx) => (
                          <div key={setting.hostname || idx} className="flex items-center justify-between p-3 rounded-lg border border-stone-200 bg-stone-50">
                            <div>
                              <div className="flex items-center gap-2">
                                <Badge className="bg-green-600 text-[10px]">Active</Badge>
                                <span className="text-sm font-medium font-mono">{setting.hostname}</span>
                              </div>
                              <p className="text-xs text-muted-foreground mt-1 break-all">{setting.url}</p>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteInboundParse(setting.hostname)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50 shrink-0"
                              data-testid={`delete-parse-${idx}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                        <p className="text-xs text-muted-foreground">
                          Replies sent to <strong>*@{emailProvider.reply_to_domain}</strong> will be forwarded to your webhook for AI classification.
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <p className="text-sm text-muted-foreground">
                          Set up Inbound Parse to receive reply emails via webhook. When a lead replies to your outreach, 
                          SendGrid forwards the email to Rise for automatic AI classification.
                        </p>
                        <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
                          <p className="text-xs text-amber-800">
                            <strong>DNS Required:</strong> Add an MX record for <code className="bg-amber-100 px-1 rounded">{emailProvider.reply_to_domain || 'replies.yourdomain.com'}</code> pointing to <code className="bg-amber-100 px-1 rounded">mx.sendgrid.net</code> (priority 10).
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Input
                            placeholder={emailProvider.reply_to_domain || 'replies.yourdomain.com'}
                            value={inboundHostname}
                            onChange={(e) => setInboundHostname(e.target.value)}
                            className="font-mono text-sm"
                            data-testid="inbound-hostname-input"
                          />
                          <Button
                            onClick={handleSetupInboundParse}
                            disabled={setupInboundParse}
                            className="bg-forest hover:bg-forest-dark text-white shrink-0"
                            data-testid="setup-inbound-parse-btn"
                          >
                            {setupInboundParse ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Activate'}
                          </Button>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Notification Preferences (Admin Only) */}
      {adminStatus.isAdmin && (
        <Card className="bg-white border-stone-100" data-testid="notification-preferences">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <Mail className="h-5 w-5 text-forest" />
                  Notification Preferences
                </CardTitle>
                <CardDescription>
                  Choose which events trigger admin email notifications and how they're delivered.
                </CardDescription>
              </div>
              {notifQueue.unsent_count > 0 && (
                <Badge className="bg-amber-500">{notifQueue.unsent_count} pending</Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-5">
            {notifLoading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : notifPrefs ? (
              <>
                {/* Event toggles */}
                <div className="space-y-3">
                  {Object.entries(notifPrefs.available_events || {}).map(([key, label]) => {
                    const pref = notifPrefs.events?.[key] || { enabled: false, mode: 'batch' };
                    return (
                      <div key={key} className="flex items-center justify-between p-3 rounded-lg border border-stone-200 hover:bg-stone-50 transition-colors" data-testid={`notif-pref-${key}`}>
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <button
                            onClick={() => toggleNotifEvent(key, 'enabled', !pref.enabled)}
                            className={`w-10 h-6 rounded-full transition-colors relative shrink-0 ${pref.enabled ? 'bg-forest' : 'bg-stone-300'}`}
                            data-testid={`notif-toggle-${key}`}
                          >
                            <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${pref.enabled ? 'left-[18px]' : 'left-0.5'}`} />
                          </button>
                          <span className={`text-sm font-medium ${pref.enabled ? 'text-foreground' : 'text-muted-foreground'}`}>{label}</span>
                        </div>
                        {pref.enabled && (
                          <Select
                            value={pref.mode}
                            onValueChange={(value) => toggleNotifEvent(key, 'mode', value)}
                          >
                            <SelectTrigger className="w-28 h-8 text-xs" data-testid={`notif-mode-${key}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="realtime" className="text-xs">Real-time</SelectItem>
                              <SelectItem value="batch" className="text-xs">Batch</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    );
                  })}
                </div>

                <Separator />

                {/* Batch settings */}
                <div className="p-3 rounded-lg bg-stone-50 border border-stone-200">
                  <p className="text-xs text-muted-foreground mb-1">Batch Digest</p>
                  <p className="text-sm text-muted-foreground">
                    Events set to <strong>Batch</strong> are collected and sent as a summary email. 
                    Use the button below to send the digest now, or it can be triggered on schedule.
                  </p>
                  {notifQueue.unsent_count > 0 && (
                    <Button
                      onClick={handleSendDigest}
                      disabled={sendingDigest}
                      variant="outline"
                      size="sm"
                      className="mt-3"
                      data-testid="send-digest-btn"
                    >
                      {sendingDigest ? (
                        <><Loader2 className="mr-2 h-3 w-3 animate-spin" />Sending...</>
                      ) : (
                        <>Send Digest Now ({notifQueue.unsent_count} events)</>
                      )}
                    </Button>
                  )}
                </div>

                <Button
                  onClick={handleSaveNotifPrefs}
                  disabled={savingNotifPrefs}
                  className="w-full bg-forest hover:bg-forest-dark text-white"
                  data-testid="save-notif-prefs-btn"
                >
                  {savingNotifPrefs ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving...</>
                  ) : (
                    <><Save className="mr-2 h-4 w-4" />Save Notification Preferences</>
                  )}
                </Button>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">Unable to load notification preferences.</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Access Control (Admin Only) */}
      {adminStatus.isAdmin && (
        <Card className="bg-white border-stone-100 border-2 border-forest/20">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Building2 className="h-5 w-5 text-forest" />
              Access Control
              <Badge className="bg-forest ml-2">Admin</Badge>
            </CardTitle>
            <CardDescription>
              Manage who can access this application. Only users in the allowlist can sign in.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Status */}
            <div className="p-4 rounded-lg bg-sage/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Access Control Status</p>
                  <p className="text-sm text-muted-foreground">
                    {adminStatus.accessControlEnabled 
                      ? "Enabled - Only allowed emails can sign in"
                      : "Disabled - Anyone with Google can sign in"}
                  </p>
                </div>
                <Badge variant={adminStatus.accessControlEnabled ? "default" : "secondary"} className={adminStatus.accessControlEnabled ? "bg-green-600" : ""}>
                  {adminStatus.accessControlEnabled ? "Protected" : "Open"}
                </Badge>
              </div>
              {!adminStatus.accessControlEnabled && (
                <p className="text-xs text-amber-700 mt-2 p-2 bg-amber-50 rounded">
                  To enable access control, add your email to ALLOWED_EMAILS in the backend .env file.
                </p>
              )}
            </div>

            {/* Add New Email */}
            <div>
              <Label className="mb-2 block">Add Allowed User</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="email@example.com"
                  value={newAllowedEmail}
                  onChange={(e) => setNewAllowedEmail(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddAllowedEmail()}
                />
                <Button
                  onClick={handleAddAllowedEmail}
                  disabled={addingEmail || !newAllowedEmail.trim()}
                  className="bg-forest hover:bg-forest-dark text-white"
                >
                  {addingEmail ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add"}
                </Button>
              </div>
            </div>

            {/* Allowed Emails List */}
            <div>
              <Label className="mb-2 block">Allowed Users ({accessControl.allowedEmails?.length || 0})</Label>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {accessControl.allowedEmails?.length === 0 ? (
                  <p className="text-sm text-muted-foreground italic">No users in allowlist</p>
                ) : (
                  accessControl.allowedEmails?.map((item, idx) => (
                    <div key={item.email} className="flex items-center justify-between p-2 bg-stone-50 rounded">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-sm truncate">{item.email}</span>
                        {item.email === accessControl.adminEmail && (
                          <Badge variant="outline" className="text-xs">Admin</Badge>
                        )}
                        {item.source === 'environment' && (
                          <Badge variant="secondary" className="text-xs">.env</Badge>
                        )}
                      </div>
                      {item.source !== 'environment' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveAllowedEmail(item.email)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Registered Users */}
            {accessControl.registeredUsers?.length > 0 && (
              <div>
                <Label className="mb-2 block">Registered Users ({accessControl.registeredUsers.length})</Label>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {accessControl.registeredUsers.map((regUser, idx) => (
                    <div key={regUser.email} className="flex items-center justify-between p-2 bg-stone-50 rounded">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-sm truncate">{regUser.email}</span>
                        <span className="text-xs text-muted-foreground">{regUser.name}</span>
                        {regUser.is_admin && (
                          <Badge variant="outline" className="text-xs">Admin</Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* About */}
      <Card className="bg-white border-stone-100">
        <CardHeader>
          <CardTitle className="font-heading text-lg">About Rise</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-sm">
            Rise is a lead generation and CRM tool designed for wholesale bakeries. 
            Discover potential clients, analyze them with AI, and send personalized outreach to grow your business.
          </p>
          <div className="mt-4 flex items-center gap-2">
            <div className="w-8 h-8 bg-forest rounded-lg flex items-center justify-center">
              <span className="text-white font-bold font-heading text-sm">R</span>
            </div>
            <span className="font-heading font-bold text-forest">Rise</span>
            <span className="text-muted-foreground text-sm">v1.0.0</span>
          </div>
        </CardContent>
      </Card>

      {/* Data Hygiene — Sync Categories */}
      <Card className="bg-white border-stone-100" data-testid="sync-categories-card">
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <RefreshCcw className="h-5 w-5 text-forest" />
            Sync Categories from Segments
          </CardTitle>
          <CardDescription>
            Aligns each lead's <span className="font-mono text-xs">category</span> field with its{' '}
            <span className="font-mono text-xs">segment_name</span> so the Pipeline category filter
            matches what you see on lead cards. Segment is treated as the source of truth.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between gap-4">
            <div className="text-sm">
              {loadingMismatches ? (
                <span className="text-muted-foreground inline-flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Checking your leads…
                </span>
              ) : categoryMismatchCount === null ? (
                <span className="text-muted-foreground">Couldn't load mismatch count.</span>
              ) : categoryMismatchCount === 0 ? (
                <span className="text-forest inline-flex items-center gap-2" data-testid="sync-categories-aligned">
                  <Check className="h-4 w-4" />
                  All leads aligned — nothing to sync.
                </span>
              ) : (
                <span data-testid="sync-categories-mismatch-count">
                  <span className="font-semibold">{categoryMismatchCount}</span>{' '}
                  {categoryMismatchCount === 1 ? 'lead has' : 'leads have'} a category that doesn't match its segment.
                </span>
              )}
            </div>
            <Button
              onClick={handleSyncCategories}
              disabled={syncingCategories || loadingMismatches || !categoryMismatchCount}
              className="bg-forest hover:bg-forest-dark text-white"
              data-testid="sync-categories-btn"
            >
              {syncingCategories ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Syncing…
                </>
              ) : (
                <>
                  <RefreshCcw className="mr-2 h-4 w-4" />
                  Sync Now
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* SendGrid Setup Wizard Modal */}
      <SendGridSetupWizard
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        onComplete={() => { fetchEmailProvider(); }}
      />
    </div>
  );
};

export default Settings;
